import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAgency } from '@/contexts/AgencyContext';

export type SubStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'paused' | 'payment_required';

export interface SubscriptionState {
  loading: boolean;
  agency: any | null;
  status: SubStatus | null;
  isTrialing: boolean;
  trialDaysRemaining: number;
  trialEndsAt: Date | null;
  isLocked: boolean; // workspace access blocked
  reminderTier: 'none' | 'd7' | 'd2' | 'd1' | 'd0';
  reminderMessage: string | null;
  canAccessBilling: boolean;
}

const LOCKED_STATUSES: SubStatus[] = ['payment_required', 'paused', 'canceled'];

export function useSubscriptionStatus(): SubscriptionState {
  const { agencyId, isSuperAdmin } = useAgency();

  const { data: agency, isLoading } = useQuery({
    queryKey: ['subscription-status', agencyId],
    queryFn: async () => {
      if (!agencyId) return null;
      const { data } = await supabase
        .from('agencies')
        .select('id, name, status, plan, trial_started_at, trial_ends_at, subscription_status, payment_status, billing_provider, grace_period_ends_at, last_payment_at, canceled_at')
        .eq('id', agencyId)
        .maybeSingle();
      return data;
    },
    enabled: !!agencyId,
    staleTime: 60_000,
  });

  if (isSuperAdmin) {
    return {
      loading: false, agency, status: 'active', isTrialing: false,
      trialDaysRemaining: 0, trialEndsAt: null, isLocked: false,
      reminderTier: 'none', reminderMessage: null, canAccessBilling: true,
    };
  }

  const a: any = agency ?? null;
  const status = (a?.subscription_status ?? null) as SubStatus | null;
  const trialEndsAt = a?.trial_ends_at ? new Date(a.trial_ends_at) : null;
  const now = Date.now();
  const days = trialEndsAt ? Math.ceil((trialEndsAt.getTime() - now) / 86_400_000) : 0;

  const trialExpired = status === 'trialing' && trialEndsAt && trialEndsAt.getTime() < now;
  const isLocked = !!a && (LOCKED_STATUSES.includes(status as SubStatus) || !!trialExpired);

  let reminderTier: SubscriptionState['reminderTier'] = 'none';
  let reminderMessage: string | null = null;
  if (status === 'trialing' && trialEndsAt) {
    if (days <= 0) { reminderTier = 'd0'; reminderMessage = 'Your trial has ended. Add payment to continue.'; }
    else if (days <= 1) { reminderTier = 'd1'; reminderMessage = 'Your trial ends tomorrow.'; }
    else if (days <= 2) { reminderTier = 'd2'; reminderMessage = `Your trial ends in ${days} days.`; }
    else if (days <= 7) { reminderTier = 'd7'; reminderMessage = `${days} days left in your trial.`; }
  }

  return {
    loading: isLoading,
    agency: a,
    status,
    isTrialing: status === 'trialing' && !trialExpired,
    trialDaysRemaining: Math.max(0, days),
    trialEndsAt,
    isLocked,
    reminderTier,
    reminderMessage,
    canAccessBilling: true,
  };
}
